import { createStorage, setStorage, STORAGE_KEYS } from './storage';
import { type DeviceInfo } from './device';
import request from './request';
import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';

const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCXQ8fUn3aJfePWl+72zueR1/favtGo5qF/9ojsui027QwU4yod7joKVPG7Lg4Cdxp9UQCXji6jJ5a7kAWb9NWrq0mss3t491w2H3Ff/xjIIcWIjf8avMqB3Al1FpOONIM7nmIEhO7PdHnRlDLvBhnT+7Txe4Euw3dqZHKEarUk1QIDAQAB
-----END PUBLIC KEY-----`;

let aidStorage = createStorage(STORAGE_KEYS.AID, '');
let fingerprintStorage = createStorage<{ aid: string; vgsid: string } | null>(STORAGE_KEYS.FINGERPRINT, null);

/**
 * 生成AES加密密码（时间戳13位 + 随机数据）
 */
function generateAESPassword(): { key: string; iv: string } {
  const timestamp = Date.now().toString().slice(0, 13); // 13位时间戳
  const randomData = CryptoJS.lib.WordArray.random(8).toString(CryptoJS.enc.Hex); // 8字节随机数据转为16位十六进制字符串

  return {
    key: timestamp,
    iv: randomData,
  };
}

/**
 * 使用AES加密数据
 */
function encryptDataWithAES(data: Record<string, any>, key: string, iv: string): string {
  try {
    // 使用AES-256-CBC模式加密，确保填充方式正确
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), CryptoJS.enc.Utf8.parse(iv), {
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
      iv: CryptoJS.enc.Utf8.parse(iv),
    });

    return encrypted.toString();
  } catch (error) {
    console.error('AES encryption failed:', error);
    throw new Error('AES encryption failed');
  }
}

/**
 * 使用RSA公钥加密密码
 */
function encryptPasswordWithRSA(password: string): string {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(RSA_PUBLIC_KEY);
  const encrypted = encrypt.encrypt(password).toString();

  if (!encrypted) {
    throw new Error('RSA encryption failed');
  }
  return encrypted;
}

/**
 * 混合加密方案：AES加密数据 + RSA加密密码
 * @param data 要加密的原始数据
 * @returns {sign: string, data: string} sign为RSA加密的密码，data为AES加密的数据
 */
export function hybridEncrypt(data: Record<string, any>): { sign: string; data: string } {
  // 第一步：生成AES密码
  const { key, iv } = generateAESPassword();
  const aesPassword = key + iv;

  // 第二步：使用AES密码加密数据
  const encryptedData = encryptDataWithAES(data, key, iv);
  console.log('AES encrypted data length:', encryptedData.length);

  // 第三步：使用RSA公钥加密AES密码
  const encryptedPassword = encryptPasswordWithRSA(aesPassword);

  return {
    sign: encryptedPassword, // RSA加密后的密码
    data: encryptedData, // AES加密后的数据
  };
}

export async function getFingerprint(deviceInfo: DeviceInfo): Promise<{
  aid: string;
  // t_token: string;
  // random_string: string;
  vgsid: string;
} | null> {
  if (!aidStorage) {
    aidStorage = setStorage(STORAGE_KEYS.AID, generateAESPassword().iv);
  }
  const payload = {
    ...deviceInfo,
    uuid: aidStorage,
  };
  const { sign, data } = hybridEncrypt(payload);
  try {
    const { data: res } = await request.post(
      // '/rd/logs/is/cc/wb',
      '/sso/api/device/wb',
      { data },
      {
        headers: {
          sign,
        },
      },
    );
    fingerprintStorage = setStorage(STORAGE_KEYS.FINGERPRINT, { aid: res.aid, vgsid: res.vgsid });
  } catch (error) {
    console.error('get fingerprint failed:', error);
  }
  return fingerprintStorage;
}
